// frontend/components/settings/AdminSlackTab.tsx
//
// 관리자 전용: Slack Incoming Webhook URL 설정 탭

"use client";

import { useState, useEffect } from "react";
import { Save, CheckCircle, AlertCircle, ExternalLink } from "lucide-react";
import { API_URL, authHeaders, authOnlyHeaders } from "../../utils/api";

interface AdminSlackTabProps {
  accessToken?: string;
}

export default function AdminSlackTab({ accessToken }: AdminSlackTabProps) {
  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState<{ type: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    fetch(`${API_URL}/system/slack-config`, { headers: authOnlyHeaders(accessToken) })
      .then(r => r.json())
      .then(d => setWebhookUrl(d.webhookUrl || ""))
      .catch(() => {});
  }, [accessToken]);

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch(`${API_URL}/system/slack-config`, {
        method: "PUT",
        headers: authHeaders(accessToken),
        body: JSON.stringify({ webhookUrl }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg({ type: "err", text: data.error || "저장 실패" });
      } else {
        setMsg({ type: "ok", text: "저장되었습니다." });
        setTimeout(() => setMsg(null), 3000);
      }
    } catch {
      setMsg({ type: "err", text: "네트워크 오류가 발생했습니다." });
    }
    setSaving(false);
  };

  const inputCls = "w-full px-3 py-2 text-sm bg-white dark:bg-zinc-950 border border-gray-200 dark:border-zinc-700 rounded-lg outline-none focus:ring-1 focus:ring-blue-400 dark:focus:ring-blue-500 text-gray-800 dark:text-zinc-100 font-mono";

  return (
    <div className="space-y-5">
      <div>
        <p className="text-base font-bold text-gray-800 dark:text-zinc-100 mb-1">Slack 알림 연동</p>
        <p className="text-xs text-gray-400 dark:text-zinc-500">
          사용자가 직급 변경 또는 비밀번호 초기화를 요청할 때 지정된 Slack 채널로 알림이 전송됩니다.
        </p>
      </div>

      {/* 설정 가이드 */}
      <div className="p-3 bg-blue-50 dark:bg-blue-900/15 border border-blue-200 dark:border-blue-800 rounded-lg text-xs text-blue-700 dark:text-blue-300 space-y-1">
        <p className="font-semibold">Slack Incoming Webhook URL 발급 방법</p>
        <ol className="list-decimal list-inside space-y-0.5 text-blue-600 dark:text-blue-400">
          <li>Slack 워크스페이스에서 앱 디렉터리 → Incoming Webhooks 검색</li>
          <li>채널 선택 후 "Incoming Webhook 추가"</li>
          <li>생성된 Webhook URL을 아래에 붙여넣기</li>
        </ol>
        <a
          href="https://api.slack.com/messaging/webhooks"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 mt-1 underline hover:text-blue-800 dark:hover:text-blue-200"
        >
          Slack 공식 가이드 <ExternalLink size={11} />
        </a>
      </div>

      {/* URL 입력 */}
      <div>
        <label className="block text-xs font-medium text-gray-500 dark:text-zinc-400 mb-1">
          Webhook URL
        </label>
        <input
          type="url"
          value={webhookUrl}
          onChange={e => setWebhookUrl(e.target.value)}
          placeholder="https://hooks.slack.com/services/..."
          className={inputCls}
        />
        <p className="mt-1 text-xs text-gray-400 dark:text-zinc-500">
          비워두면 Slack 알림이 비활성화됩니다.
        </p>
      </div>

      {/* 저장 버튼 */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#006DBA] hover:bg-[#005A9E] disabled:opacity-60 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Save size={14} />
          {saving ? "저장 중…" : "저장"}
        </button>
        {msg && (
          <span className={`flex items-center gap-1 text-xs ${msg.type === "ok" ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400"}`}>
            {msg.type === "ok" ? <CheckCircle size={13} /> : <AlertCircle size={13} />}
            {msg.text}
          </span>
        )}
      </div>

      {/* 상태 표시 */}
      <div className="pt-3 border-t border-gray-100 dark:border-zinc-800">
        <p className="text-xs text-gray-400 dark:text-zinc-500">
          현재 상태:{" "}
          <span className={webhookUrl ? "text-green-600 dark:text-green-400 font-medium" : "text-gray-400"}>
            {webhookUrl ? "연동됨" : "미설정"}
          </span>
        </p>
      </div>
    </div>
  );
}
